import React from 'react';
import { Select, DatePicker, Input, Segmented, Switch } from 'antd';
import { Search, SlidersHorizontal } from 'lucide-react';
import { FilterBar, FilterField, StatusTag } from '../../common';
import { SOURCE_OPTIONS, ROLE_OPTIONS, CHANNEL_OPTIONS, MAYORISTA_OPTIONS, countActiveFilters } from '../levantamientoShared';
import { useCommittedSearch } from './levantamientoFilters.controller';
import { brand } from '../../../theme/tokens';

const { RangePicker } = DatePicker;

/** Free-text search that commits on Enter (or when cleared). */
function SearchInput({ value, onCommit }) {
    const { text, onChange, onPressEnter } = useCommittedSearch(value, onCommit);
    return (
        <Input
            allowClear
            prefix={<Search className="text-surface-400" />}
            placeholder="Producto, piso o cliente"
            value={text}
            onChange={onChange}
            onPressEnter={onPressEnter}
        />
    );
}

/**
 * Labeled block for controls that shouldn't sit inside a <label> (range pickers and segmented
 * controls contain several inputs, so label click-forwarding would hit the wrong one).
 */
const FieldBlock = ({ label, children, className = '' }) => (
    <div className={`flex flex-col gap-1 min-w-0 ${className}`}>
        <span className="text-xs font-medium text-surface-500">{label}</span>
        {children}
    </div>
);

// Shared prop set for the categorical multi-select dropdowns.
const multiSelectProps = {
    mode: 'multiple',
    allowClear: true,
    showSearch: true,
    optionFilterProp: 'label',
    maxTagCount: 'responsive',
    className: 'w-full'
};

/**
 * Shared filter bar for the Levantamiento screens (list + charts).
 * `actions` renders the right-side buttons (e.g. "Limpiar"); `showSearch` toggles the free-text field.
 */
export default function LevantamientoFilters({
    filters,
    dateRange,
    clientOptions = [],
    storeOptions = [],
    userOptions = [],
    categoryOptions = [],
    segmentOptions = [],
    tagOptions = [],
    gramsOptions = [],
    storeTypeOptions = [],
    onFilter,
    onDateRange,
    showSearch = true,
    showMayorista = true,
    showOutlierToggle = false,
    actions = null
}) {
    const activeCount = countActiveFilters(filters);

    return (
        <FilterBar showLabel={false}>
            <div className="w-full">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <span className="flex items-center gap-2 text-[13px] font-medium text-surface-500">
                        <SlidersHorizontal className="text-[15px]" />
                        Filtros
                        {activeCount > 0 && (
                            <StatusTag tone="brand">
                                {activeCount} {activeCount === 1 ? 'activo' : 'activos'}
                            </StatusTag>
                        )}
                    </span>
                    {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
                </div>

                <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 2xl:grid-cols-6">
                    {showSearch ? (
                        <FilterField label="Buscar" className="sm:col-span-2">
                            <SearchInput value={filters.search} onCommit={(v) => onFilter('search', v)} />
                        </FilterField>
                    ) : null}
                    <FieldBlock label="Rango de fechas" className="sm:col-span-2">
                        <RangePicker
                            className="w-full"
                            value={dateRange}
                            allowClear={false}
                            format="DD/MM/YYYY"
                            onChange={(range) => onDateRange(range)}
                        />
                    </FieldBlock>
                    <FilterField label="Fuente">
                        <Select
                            {...multiSelectProps}
                            placeholder="Todas"
                            value={filters.source || []}
                            options={SOURCE_OPTIONS}
                            onChange={(v) => onFilter('source', v || [])}
                        />
                    </FilterField>
                    <FilterField label="Categoría">
                        <Select
                            {...multiSelectProps}
                            placeholder="Todas"
                            value={filters.category || []}
                            options={categoryOptions}
                            onChange={(v) => onFilter('category', v || [])}
                        />
                    </FilterField>
                    <FilterField label="Segmento">
                        <Select
                            {...multiSelectProps}
                            placeholder="Todos"
                            value={filters.segment || []}
                            options={segmentOptions}
                            onChange={(v) => onFilter('segment', v || [])}
                        />
                    </FilterField>
                    <FilterField label="Gramaje">
                        <Select
                            {...multiSelectProps}
                            placeholder="Todos"
                            value={filters.grams || []}
                            options={gramsOptions}
                            onChange={(v) => onFilter('grams', v || [])}
                        />
                    </FilterField>
                    <FilterField label="Tipo de PDV">
                        <Select
                            {...multiSelectProps}
                            placeholder="Todos"
                            value={filters.storeType || []}
                            options={storeTypeOptions}
                            onChange={(v) => onFilter('storeType', v || [])}
                        />
                    </FilterField>
                    <FilterField label="Cliente">
                        <Select
                            {...multiSelectProps}
                            placeholder="Todos"
                            value={filters.clientId || []}
                            options={clientOptions}
                            onChange={(v) => onFilter('clientId', v || [])}
                        />
                    </FilterField>
                    <FilterField label="Piso de venta">
                        <Select
                            {...multiSelectProps}
                            placeholder="Todos"
                            value={filters.storeId || []}
                            options={storeOptions}
                            onChange={(v) => onFilter('storeId', v || [])}
                        />
                    </FilterField>
                    <FilterField label="Usuario">
                        <Select
                            {...multiSelectProps}
                            placeholder="Todos"
                            value={filters.userId || []}
                            options={userOptions}
                            onChange={(v) => onFilter('userId', v || [])}
                        />
                    </FilterField>
                    <FilterField label="Rol">
                        <Select
                            {...multiSelectProps}
                            placeholder="Todos"
                            value={filters.userType || []}
                            options={ROLE_OPTIONS}
                            onChange={(v) => onFilter('userType', v || [])}
                        />
                    </FilterField>
                    <FilterField label="Canal">
                        <Select
                            {...multiSelectProps}
                            placeholder="Todos"
                            value={filters.channel || []}
                            options={CHANNEL_OPTIONS}
                            onChange={(v) => onFilter('channel', v || [])}
                        />
                    </FilterField>
                    <FilterField label="Etiqueta">
                        <Select
                            {...multiSelectProps}
                            placeholder="Todas"
                            value={filters.tagId || []}
                            options={tagOptions}
                            onChange={(v) => onFilter('tagId', v || [])}
                            optionRender={(opt) => (
                                <span className="flex items-center gap-2">
                                    {/* The tag's own color (data-driven). */}
                                    <span
                                        className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                                        style={{ background: opt.data.color || brand[500] }}
                                    />
                                    {opt.data.label}
                                </span>
                            )}
                        />
                    </FilterField>
                </div>

                {showMayorista || showOutlierToggle ? (
                    <div className="mt-3 pt-3 border-t border-surface-100 flex flex-wrap items-center gap-x-8 gap-y-3">
                        {showMayorista ? (
                            <div className="flex flex-wrap items-center gap-3">
                                <span className="text-xs font-medium text-surface-500">Mayoristas</span>
                                <Segmented
                                    size="small"
                                    options={MAYORISTA_OPTIONS}
                                    value={filters.mayorista || 'all'}
                                    onChange={(v) => onFilter('mayorista', v)}
                                />
                            </div>
                        ) : null}
                        {showOutlierToggle ? (
                            <div className="flex items-center gap-3">
                                <span className="text-xs font-medium text-surface-500">Atípicos</span>
                                <span className="flex items-center gap-2">
                                    <Switch
                                        size="small"
                                        checked={!!filters.hideOutliers}
                                        onChange={(checked) => onFilter('hideOutliers', checked)}
                                    />
                                    <span className="text-xs text-surface-600">Ocultar</span>
                                </span>
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </div>
        </FilterBar>
    );
}
